import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plug, RefreshCw } from "lucide-react";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useConnectorConnect,
  useConnectorSettings,
  useConnectors,
  type Connector,
  type ConnectorTool,
  type ToolPermission,
} from "@/hooks/useConnectors";
import { useFigmaConnection } from "@/hooks/useFigma";
import { useStripeOAuthConnect } from "@/hooks/useStripeOAuthConnect";
import { actionFor, matchesSearch } from "@/utils/connectorActions";
import axiosInstance from "@/utils/appsettings";
import ConnectorCredentialsDialog from "@/components/Connectors/ConnectorCredentialsDialog";
import ConnectorDetail from "@/components/Connectors/ConnectorDetail";
import ConnectorList, { type ConnectorSection } from "@/components/Connectors/ConnectorList";
import ConnectorRail, {
  FILTER_ALL,
  FILTER_ENABLED,
} from "@/components/Connectors/ConnectorRail";
import FigmaAccountPanel from "@/components/Connectors/FigmaAccountPanel";
import FigmaExpectations from "@/components/Connectors/FigmaExpectations";
import FigmaPluginPanel from "@/components/Connectors/FigmaPluginPanel";
import StripeProjectPicker from "@/components/Connectors/StripeProjectPicker";
import StripeConnectedProjects from "@/components/Connectors/StripeConnectedProjects";
import { readConnectedProjects } from "@/utils/stripeConnectedProjects";
import { useNavigate } from "react-router-dom";

// Connectors as a modal rather than a page: every one of these is something a user reaches for
// *while* describing what they want built, and sending them to a separate screen loses that thread.
//
// Two panes, because one list stops working as the catalogue grows: the rail narrows the set, the
// pane shows either the narrowed list or one connector in full. The rule the screen is built around
// is unchanged — nothing here is a dead end. Every connector either opens its real setup flow, or
// hands the user back to the prompt box with the request already written.

const REQUEST_PROMPT =
  "I'd like to use a connector Joylo doesn't have yet. Here's the service and what I want to do with it: ";

interface ConnectorsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present when opened from inside a project, which unlocks the project-scoped connectors. */
  projectId?: string;
  /** Opens a connector's own setup modal. */
  onOpenConnectorModal: (connector: string) => void;
  /** Hands the user back to the prompt box with the request written for them. */
  onPrefillPrompt: (prompt: string) => void;
  /** Takes the user to a project; hosts whose close handler navigates (the /connectors route goes back) must override it. */
  onOpenProject?: (url: string) => void;
}

const ConnectorsDialog: React.FC<ConnectorsDialogProps> = ({
  open,
  onOpenChange,
  projectId,
  onOpenConnectorModal,
  onPrefillPrompt,
  onOpenProject,
}) => {
  const { data, connectors, categories, loading, error, refresh } = useConnectors(projectId);
  const navigate = useNavigate();
  const { saving, setToolPermissions, setWorkspaceEnabled } = useConnectorSettings();
  const { connect: runConnect, error: connectError, clearError } = useConnectorConnect();
  const figma = useFigmaConnection();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<string>(FILTER_ALL);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [stripeError, setStripeError] = useState<string | null>(null);
  // Outside a project the Stripe CTA reveals a project picker instead of writing a prompt
  const [stripePickerOpen, setStripePickerOpen] = useState(false);

  // Stripe Connect: popup to Stripe, whose redirect lands on the Joylo callback that writes the keys into the project .env
  const stripeOAuth = useStripeOAuthConnect(projectId, {
    onConnected: () => refresh(),
    onFailed: (err) => {
      setStripeError(err || "Could not connect your Stripe account. Try again.");
    },
    // Stop spinning once Stripe is open; a window closed without finishing would otherwise leave the button busy
    onOpened: () => setBusyKey(null),
    onPopupBlocked: () => {
      setBusyKey(null);
      setStripeError("Allow popups for this site, then try again.");
    },
    onOpenError: () => {
      setBusyKey(null);
      setStripeError("Could not open Stripe authorisation. Try again.");
    },
  });

  // Held locally so one toggle confirms without refetching the whole screen
  const [toolOverrides, setToolOverrides] = useState<Record<string, ConnectorTool[]>>({});

  // Re-checked each time it opens, so a connector configured in another tab is not shown as missing
  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  // Reset the view on close, so reopening never lands on a stale connector or a leftover search
  useEffect(() => {
    if (!open) {
      setSearch("");
      setFilter(FILTER_ALL);
      setSelectedKey(null);
      setToolOverrides({});
      clearError();
    }
  }, [open, clearError]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if ((event.data as { type?: string })?.type === "joylo:figma-connected") void refresh();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [refresh]);

  const withOverrides = useCallback(
    (connector: Connector): Connector =>
      toolOverrides[connector.key]
        ? { ...connector, tools: toolOverrides[connector.key] }
        : connector,
    [toolOverrides],
  );

  const visible = useMemo(() => {
    const bySearch = connectors.filter((c) => matchesSearch(c, search));
    if (filter === FILTER_ALL) return bySearch;
    if (filter === FILTER_ENABLED) return bySearch.filter((c) => c.connected);
    return bySearch.filter((c) => (c.categories ?? []).includes(filter));
  }, [connectors, search, filter]);

  /**
   * Category sections for the list, matching how the screen grouped connectors before the rail.
   *
   * Only when browsing everything: inside one category the heading repeats the choice just made,
   * and across a search result it fragments the matches. Grouped on the primary category — the
   * first one the server sends — so a connector in two categories still appears exactly once.
   */
  const sections = useMemo<ConnectorSection[] | null>(() => {
    if (filter !== FILTER_ALL || search.trim()) return null;

    const labels = new Map(categories.map((c) => [c.key, c.label]));
    const order = categories.map((c) => c.key);
    const buckets = new Map<string, Connector[]>();

    for (const connector of visible) {
      const primary = connector.categories?.[0] ?? "other";
      if (!buckets.has(primary)) buckets.set(primary, []);
      buckets.get(primary)!.push(connector);
    }

    const keys = [...buckets.keys()].sort(
      (a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99),
    );
    const built = keys.map((key) => ({
      key,
      label: labels.get(key) ?? key.charAt(0).toUpperCase() + key.slice(1),
      connectors: buckets.get(key)!,
    }));

    // One section is just a flat list wearing a heading
    return built.length > 1 ? built : null;
  }, [categories, filter, search, visible]);

  const selected = useMemo(() => {
    const found = connectors.find((c) => c.key === selectedKey);
    return found ? withOverrides(found) : null;
  }, [connectors, selectedKey, withOverrides]);

  // The connector whose credential form is open. Held here rather than handed to the host for the
  // same reason the OAuth flow is: the form is generic, and the host only knows the connectors
  // that predate the framework.
  const [credentialsFor, setCredentialsFor] = useState<Connector | null>(null);

  const handlePress = useCallback(
    async (connector: Connector) => {
      const action = actionFor(connector, projectId);
      if (action.kind === "blocked") return;
      clearError();
      setStripeError(null);

      // Inside a project, Stripe goes straight to Stripe Connect; called before any await so the popup isn't blocked
      if (connector.key === "stripe" && projectId && !connector.connected) {
        setBusyKey("stripe");
        void stripeOAuth.connect();
        return;
      }
      if (connector.key === "stripe" && !projectId) {
        setStripePickerOpen(true);
        return;
      }

      // Figma is the exception: its setup is short enough to finish inline on this pane
      if (connector.key === "figma") {
        if (!connector.connected) {
          setBusyKey("figma");
          await figma.connect();
          setBusyKey(null);
        }
        return;
      }

      if (action.kind === "prompt") {
        onPrefillPrompt(action.prompt);
        onOpenChange(false);
        return;
      }

      if (action.kind === "credentials") {
        setCredentialsFor(connector);
        return;
      }

      // Handled here rather than handed to the host: the generic flow is the same for every
      // connector, and the host only knows the ones that predate it
      if (action.kind === "oauth") {
        setBusyKey(connector.key);
        const ok = await runConnect(connector.key, connector.name);
        setBusyKey(null);
        if (ok) await refresh();
        return;
      }

      onOpenConnectorModal(action.connector);
      onOpenChange(false);
    },
    [clearError, figma, onOpenChange, onOpenConnectorModal, onPrefillPrompt, projectId, refresh, runConnect, stripeOAuth],
  );

  // Adding a second account is the same flow as the first: OAuth reruns and the provider's own
  // account chooser decides which one, a credential form opens blank.
  const handleAddAccount = useCallback(
    async (connector: Connector) => {
      clearError();
      if (connector.credentialFields && connector.credentialFields.length > 0) {
        setCredentialsFor(connector);
        return;
      }
      setBusyKey(connector.key);
      const ok = await runConnect(connector.key, connector.name);
      setBusyKey(null);
      if (ok) await refresh();
    },
    [clearError, refresh, runConnect],
  );

  const handleDisconnectAccount = useCallback(
    async (connector: Connector, connectionId: string) => {
      setBusyKey(connector.key);
      try {
        await axiosInstance.delete(
          `/connectors/${connector.key}/connection`,
          { params: { connection_id: connectionId } },
        );
        await refresh();
      } catch {
        // Left to the shared error surface rather than swallowed
      } finally {
        setBusyKey(null);
      }
    },
    [refresh],
  );

  const handleToggleWorkspace = useCallback(
    async (connector: Connector, enabled: boolean) => {
      setBusyKey(connector.key);
      const ok = await setWorkspaceEnabled(connector.key, enabled, data?.workspaceId);
      setBusyKey(null);
      if (ok) await refresh();
    },
    [data?.workspaceId, refresh, setWorkspaceEnabled],
  );

  const savePermissions = useCallback(
    async (connector: Connector, permissions: Record<string, ToolPermission>) => {
      const tools = await setToolPermissions(connector.key, permissions);
      // The server's resolved value governs the agent, and a workspace "never" outranks the click
      if (tools) setToolOverrides((current) => ({ ...current, [connector.key]: tools }));
    },
    [setToolPermissions],
  );

  const handlePermissionChange = useCallback(
    (connector: Connector, toolName: string, permission: ToolPermission) =>
      void savePermissions(connector, { [toolName]: permission }),
    [savePermissions],
  );

  const handlePermissionChangeAll = useCallback(
    (connector: Connector, permission: ToolPermission) => {
      const all = Object.fromEntries((connector.tools ?? []).map((t) => [t.name, permission]));
      if (Object.keys(all).length) void savePermissions(connector, all);
    },
    [savePermissions],
  );

  const requestConnector = useCallback(() => {
    onPrefillPrompt(REQUEST_PROMPT);
    onOpenChange(false);
  }, [onOpenChange, onPrefillPrompt]);

  const connectedCount = data?.connectedCount ?? 0;
  const totalCount = data?.totalCount ?? connectors.length;
  const progress = totalCount > 0 ? Math.round((connectedCount / totalCount) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[min(88vh,46rem)] w-[calc(100vw-1.5rem)] max-w-5xl flex-col gap-0 overflow-hidden p-0 sm:w-[calc(100vw-4rem)]"
        aria-describedby={undefined}
      >
        <header className="shrink-0 border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white px-5 py-4 pr-14 sm:px-6 sm:pr-16 dark:border-white/10 dark:from-white/5 dark:to-transparent">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-ink-blue-900 text-white dark:bg-white/10">
                  <Plug className="h-3.5 w-3.5" />
                </span>
                Connectors
              </h2>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Connect the services your apps use. Once connected, Joylo builds against them and
                stops asking you for the same keys.
              </p>
            </div>

            {totalCount > 0 && (
              <div className="hidden shrink-0 text-right sm:block">
                <p className="text-xl font-semibold tabular-nums text-foreground">
                  {connectedCount}
                  <span className="text-sm font-normal text-muted-foreground">/{totalCount}</span>
                </p>
                <div
                  className="mt-1 h-1 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10"
                  role="progressbar"
                  aria-valuenow={connectedCount}
                  aria-valuemin={0}
                  aria-valuemax={totalCount}
                  aria-label={`${connectedCount} of ${totalCount} connectors connected`}
                >
                  <div
                    className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
          <ConnectorRail
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              // Searching is a browse action, so it leaves a connector's detail pane
              setSelectedKey(null);
            }}
            filter={filter}
            onFilterChange={(next) => {
              setFilter(next);
              setSelectedKey(null);
            }}
            enabledCount={connectedCount}
            totalCount={totalCount}
            categories={categories}
            isWorkspaceAdmin={data?.isWorkspaceAdmin}
            onRequestConnector={requestConnector}
            onOpenAdmin={() => {
              setFilter(FILTER_ALL);
              setSelectedKey(null);
            }}
          />

          <ScrollArea className="min-h-0 flex-1">
            <div className="px-4 py-4 sm:px-5">
              {loading && (
                <div className="space-y-2" aria-busy="true">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-white/5" />
                  ))}
                </div>
              )}

              {!loading && error && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-950/30">
                  <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
                  <Button variant="outline" size="sm" onClick={refresh} className="mt-3 gap-1.5">
                    <RefreshCw className="h-3.5 w-3.5" />
                    Try again
                  </Button>
                </div>
              )}

              {!loading && !error && selected && (
                <ConnectorDetail
                  connector={selected}
                  projectId={projectId}
                  busy={busyKey === selected.key}
                  savingPermissions={saving === selected.key}
                  isWorkspaceAdmin={data?.isWorkspaceAdmin}
                  onBack={() => setSelectedKey(null)}
                  onPress={handlePress}
                  onToggleWorkspace={handleToggleWorkspace}
                  onPermissionChange={handlePermissionChange}
                  onPermissionChangeAll={handlePermissionChangeAll}
                  onAddAccount={handleAddAccount}
                  onDisconnectAccount={handleDisconnectAccount}
                  error={connectError || stripeError}
                >
                  {selected.key === "stripe" && !projectId && stripePickerOpen && <StripeProjectPicker onConnected={() => void refresh()} />}
                  {selected.key === "stripe" && !projectId && (
                    <StripeConnectedProjects
                      projects={readConnectedProjects(selected.metadata)}
                      onOpenProject={(id) => {
                        // Lands on that project's Connectors settings, where its Stripe connection is managed
                        const url = `/projects/${id}?settings.display=true&tab=connectors`;
                        if (onOpenProject) {
                          onOpenProject(url);
                          return;
                        }
                        onOpenChange(false);
                        navigate(url);
                      }}
                    />
                  )}
                  {selected.key === "figma" && (
                    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-3.5 dark:border-white/10 dark:bg-white/5">
                      <FigmaAccountPanel
                        connection={figma.connection}
                        onReconnect={async () => {
                          await figma.reconnect();
                          await refresh();
                        }}
                        onDisconnect={async () => {
                          await figma.disconnect();
                          await refresh();
                        }}
                      />
                      <FigmaPluginPanel
                        seatSupportsImport={selected.metadata?.seatSupportsImport !== false}
                        connected={selected.connected}
                      />
                      <FigmaExpectations />
                    </div>
                  )}
                </ConnectorDetail>
              )}

              {!loading && !error && !selected && (
                <ConnectorList
                  connectors={visible}
                  sections={sections}
                  search={search}
                  onSelect={(c) => setSelectedKey(c.key)}
                  onClearSearch={() => setSearch("")}
                  onRequestConnector={requestConnector}
                />
              )}
            </div>
          </ScrollArea>
        </div>

        <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/80 px-5 py-3 sm:px-6 dark:border-white/10 dark:bg-white/5">
          <p className="text-[11px] text-muted-foreground">
            More connectors are on the way. Ask in chat for anything missing.
          </p>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </footer>
      </DialogContent>

      {/* Rendered outside DialogContent so the form sits above this pane rather than inside its
          scroll area */}
      <ConnectorCredentialsDialog
        connector={credentialsFor}
        onClose={() => setCredentialsFor(null)}
        onSaved={refresh}
      />
    </Dialog>
  );
};

export default ConnectorsDialog;
